import produce, { original } from 'immer'
import {
  concat,
  fromPairs,
  get,
  intersection,
  isEmpty,
  isEqual,
  isFunction,
  keys,
  map,
  noop,
  reverse,
  set,
  size,
  slice,
  xor,
} from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Index, IndexRange, Table as VirtualizedTable } from 'react-virtualized'
import styled from 'styled-components'

import { usePrevious } from '../../hooks'
import LoaderOverlay from '../LoaderOverlay'
import Table from '../Table'
import ColumnInfinity from './ColumnInfinity'
import InfinityLoader from './InfinityLoader'
import RowCount from './RowCount'
import { InfinityConfig, TColumn, TMobileToggleState } from './types'

const PAGE_SIZE = 15 // only for UI
const ROW_HEIGHT = 50
const HEADER_HEIGHT = 40
const MOBILE_ROW_HEIGHT = 150
const MOBILE_ROW_HEIGHT_COLLAPSED = 35

const Wrapper = styled.div`
  flex: 1 1 auto;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`

const AutoSizerWrapper = styled.div`
  flex: 1 1 auto;
  height: inherit;
  min-height: 0;
`

type TRefTable = {
  recomputeRowHeights: (index?: string) => void
}

type TWaiter = {
  startIndex: number
  stopIndex: number
  resolve: (value?: any) => void
}

const InfinityTable = (props: InfinityConfig): React.ReactElement => {
  const {
    items = [],
    totalItemCount = 0,
    columns = [],
    rowHeight = ROW_HEIGHT,
    mobileRowHeight = MOBILE_ROW_HEIGHT,
    mobileRowHeightCollapsed = MOBILE_ROW_HEIGHT_COLLAPSED,
    headerHeight = HEADER_HEIGHT,
    sorts = [],
    request = noop,
    ui = {
      isDetailOpen: false,
      currentRow: 0,
    },
    saveUi = noop,
    customRowDataKey,
    applyFilter = noop,
    setSort = noop,
    sortableColumns,
    mobileColumn = undefined,
    mobileCellHeader,
    mobileCellBody,
    mobileCellHeaderItems,
    renderMobile = false,
    blockInitialRequest = false,
    displayOnly = false,
    customNoRows = undefined,
    applyLocalSort,
    useLocalStateFiltering,
    hideHeader,
    enableSelection,
    selectionKey = 'id',
    initialSelection = [],
    onChangeSelection = noop,
    customHeaders = {},
    setCustomHeaders = noop,
    customPayload = {},
    setCustomPayload = noop,
    fulltextSearchLoading,
    allIds: originalAllIds = [],
    selectedIds: externalSelectedIds,
    selectionLimit = 0,
    getIsSelectable,
    direction,
    currentRequestId,
  } = props

  const allIds = useMemo(
    (): Array<string> => map(originalAllIds, (id) => String(id)),
    [originalAllIds],
  )

  const refLoader = useRef<any>(null)
  const [refTable, setRefTable] = useState<VirtualizedTable>()
  const [currentRow, setCurrentRow] = useState(0)
  const [, setCurrentPage] = useState(0)
  const [waiters, setWaiters] = useState<TWaiter[]>([])
  const [mobileToggleState, setMobileToggleState] =
    useState<TMobileToggleState>({})

  const previousCustomHeaders = usePrevious(customHeaders)

  useEffect(() => {
    if (
      !previousCustomHeaders ||
      (previousCustomHeaders && !isEqual(customHeaders, previousCustomHeaders))
    ) {
      setCustomHeaders(customHeaders)
      setCustomPayload(customPayload)
      if (!blockInitialRequest) {
        if (!keys(items).length) {
          request({ startIndex: 0, stopIndex: 1 })
        }
      }
      setCurrentRow(get(ui, 'currentRow') as number)
    }
    return () => {
      saveUi({
        currentRow,
      })
    }
  }, [customHeaders])

  useEffect((): void => {
    if (displayOnly) {
      return
    }

    // resolving waiters
    waiters.forEach((waiter) => {
      if (items[waiter.startIndex] && items[waiter.stopIndex]) {
        waiter.resolve()
      } else {
        setWaiters([...waiters, waiter])
      }
    })
    setWaiters(waiters)
    // reset table on clear

    if (
      (keys(items).length > 0 && keys(items).length < 1) ||
      keys(items).length === 0
    ) {
      if (refLoader && refLoader.current) {
        refLoader.current.resetLoadMoreRowsCache()
      }
    }
  }, [items])

  const isRowLoaded = ({ index }: Index): boolean => {
    return items[index] !== undefined
  }

  const loadMoreRows = ({
    startIndex,
    stopIndex,
  }: IndexRange): Promise<any> => {
    // console.log("REQUESTING MORE ROWS", startIndex, stopIndex)

    request({ startIndex, stopIndex })
    const waiter = {
      startIndex,
      stopIndex,
      resolve: (value: any) => {
        // console.log()
      },
    }
    const promise = new Promise((resolve) => {
      waiter.resolve = resolve
    })
    setWaiters([...waiters, waiter])

    return promise
  }

  const onRowsRenderedCustom = ({ startIndex }: IndexRange): void => {
    if (!renderMobile && refTable) {
      refTable.recomputeRowHeights()
    }

    setCurrentRow(startIndex)
  }

  const calculatedMobileRowHeight = ({ index }: Index): number => {
    const isToggled = get(mobileToggleState, index, false)

    if (isToggled) {
      return isFunction(mobileRowHeight)
        ? mobileRowHeight(index)
        : mobileRowHeight
    }

    return mobileRowHeightCollapsed
  }

  // artifical column for checkboxes
  const checkColumn: TColumn | null =
    enableSelection && !renderMobile
      ? {
          isCheckColumn: true,
          flexGrow: 0,
          width: 30,
          dataKey: '',
          sortKey: '',
          colWidth: 0,
          label: '',
        }
      : null

  // selection state: { [id]: true/false }
  const [selectedItems, setSelectedItems] = useState<any>({})

  // ids of selected items
  const selectedIds = useMemo(
    (): Array<string> =>
      map(selectedItems, (value, key) => ({ value, key }))
        .filter((s) => s.value)
        .map((s) => s.key)
        .map(String),
    [selectedItems],
  )

  // unselect obsolete elements
  useEffect((): void => {
    const intersect = intersection(selectedIds, allIds).map(String)
    if (!isEqual(intersect, selectedIds)) {
      const newSelection = fromPairs(intersect.map((s) => [s, true]))
      setSelectedItems(newSelection)
    }
  }, [items])

  // fire on selection changes
  useEffect((): void => {
    onChangeSelection(selectedIds)
  }, [selectedIds])

  const setSelectedItemsViaIds = (ids: Array<string>): void => {
    setSelectedItems(fromPairs(ids.map((id) => [id, true])))
  }

  // detect external manipulation
  const previousExternalSelectedIds = usePrevious(externalSelectedIds)
  useEffect((): void => {
    if (!isEqual(previousExternalSelectedIds, externalSelectedIds)) {
      setSelectedItemsViaIds(externalSelectedIds ? externalSelectedIds : [])
    }
  }, [externalSelectedIds])

  // checkbox state for the toggle-all checkbox
  const toggleAllState = useMemo((): 'indeterminate' | boolean => {
    if (isEmpty(selectedIds)) {
      return false
    }
    if (
      !isEmpty(xor(selectedIds, allIds)) &&
      (selectionLimit < 1 || size(selectedIds) < selectionLimit)
    ) {
      return 'indeterminate'
    }
    return true
  }, [selectedIds, allIds])

  // change selection state of one item
  const toggleItem = (id: number): void => {
    const isSelected = get(selectedItems, id)
    if (
      !isSelected &&
      selectionLimit > 0 &&
      size(selectedIds) >= selectionLimit
    ) {
      return
    }
    setSelectedItems(
      produce((draft: object) => {
        set(draft, [id], !get(draft, id))
      }),
    )
  }

  // change selection state of all items
  const toggleAll = (value: boolean): void => {
    if (value) {
      const ids = reverse(
        selectionLimit > 0 ? slice(allIds, 0, selectionLimit) : allIds,
      )
      const newSelectedItems = fromPairs(ids.map((id) => [id, true]))
      setSelectedItems(newSelectedItems)
    } else {
      setSelectedItems({})
    }
  }

  const toggleMobileState = (index: number): void => {
    setMobileToggleState(
      produce((draft) => {
        set(draft, index, !get(original(draft), index, false))
      }),
    )
    if (refTable) {
      refTable.recomputeRowHeights(index)
    }
  }

  const limitReached = useMemo(
    (): boolean => selectionLimit > 0 && size(selectedIds) >= selectionLimit,
    [selectionLimit, selectedIds],
  )

  return (
    <Wrapper>
      <div className="d-flex flex-fill">
        <AutoSizerWrapper>
          <LoaderOverlay isLoading={fulltextSearchLoading} />
          <InfinityLoader
            displayOnly={displayOnly}
            setRef={refLoader}
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={totalItemCount}
            minimumBatchSize={10}
            threshold={15}
          >
            {({ onRowsRendered, registerChild }) => (
              <Table
                setRef={(ref: VirtualizedTable) => {
                  registerChild(ref)
                  setRefTable(ref)
                }}
                headerHeight={hideHeader ? 0 : headerHeight}
                rowCount={totalItemCount}
                rowHeight={renderMobile ? calculatedMobileRowHeight : rowHeight}
                rowGetter={({ index }) => items[index]}
                onRowsRendered={(subProps: IndexRange) => {
                  onRowsRenderedCustom(subProps)
                  onRowsRendered(subProps)
                }}
                noRowsRenderer={customNoRows}
                hideHeader={hideHeader}
              >
                {concat(
                  checkColumn,
                  renderMobile && mobileColumn !== undefined
                    ? mobileColumn
                    : columns,
                )
                  .filter(Boolean)
                  .map((column: TColumn) =>
                    ColumnInfinity({
                      column: column,
                      selectionLimit: selectionLimit,
                      selectedItems: selectedItems,
                      columns: columns,
                      renderMobile: renderMobile,
                      mobileCellHeader: mobileCellHeader,
                      mobileCellBody: mobileCellBody,
                      mobileCellHeaderItems: mobileCellHeaderItems,
                      mobileToggleState: mobileToggleState,
                      sorts: sorts,
                      applyFilter: applyFilter,
                      setSort: setSort,
                      sortableColumns: sortableColumns,
                      applyLocalSort: applyLocalSort,
                      useLocalStateFiltering: useLocalStateFiltering,
                      ui: ui,
                      setCurrentRow: setCurrentRow,
                      selectionKey: selectionKey,
                      getIsSelectable: getIsSelectable,
                      customRowDataKey: customRowDataKey,
                      toggleAll: toggleAll,
                      toggleAllState: toggleAllState,
                      limitReached: limitReached,
                      toggleItem: toggleItem,
                      toggleMobileState: toggleMobileState,
                      direction: direction,
                      t: i18n.t,
                    }),
                  )}
              </Table>
            )}
          </InfinityLoader>
        </AutoSizerWrapper>
      </div>
      <RowCount count={totalItemCount} />
    </Wrapper>
  )
}

export default InfinityTable
